import React, { useEffect, useState, useRef } from "react";
import styles from "./FreeFormModal.module.scss";
import RichTextEditor from "@/components/HtmlEditor/RichTextEditor";

import {
  createFreeFormAPI,
  fetchFreeFormByIdAPI,
  updateFreeFormAPI,
} from "@/services/api/freeForm";

import { useDispatch, useSelector } from "react-redux";
import { fetchQuestionsByTags } from "@/store/slices/QuestionSlice";
import type { RootState } from "@/store/rootReducer";
import type { AppDispatch } from "@/store/store";
import DeleteIcon from "@/components/SVGComponents/DeleteIcon";
import ReactSelect from "react-select";

interface Props {
  onClose: () => void;
  editId?: string | null;
  refreshList: () => void;
}

interface Tag {
  id: number;
  name: string;
}

const FreeFormModal: React.FC<Props> = ({
  onClose,
  editId,
  refreshList,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);

  const { questionTags } = useSelector(
    (state: RootState) => state.questions
  );

  const [name, setName] = useState("");
  const [difficulty, setDifficulty] = useState("EASY");
  const [tags, setTags] = useState<Tag[]>([]);
  const [questions, setQuestions] = useState<{ id: number, text: string }[]>([{ id: Date.now(), text: "" }]); // ✅ array of objects with unique ids
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const addFreeForm = () => {
    setQuestions((prev) => [...prev, { id: Date.now(), text: "" }]);
    shouldScrollRef.current = true;
  };

  const removeQuestion = (id: number) => {
    setDeleteId(id);
  };

  const confirmRemoveQuestion = () => {
    if (deleteId !== null) {
      setQuestions((prev) => prev.filter((q) => q.id !== deleteId));
      setDeleteId(null);
    }
  };

  const updateQuestion = (id: number, value: string) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, text: value } : q))
    );
  };

  const handleTagSelect = (id: number) => {
    const selected = questionTags.find((t: Tag) => t.id === id);

    if (
      selected &&
      tags.length < 5 &&
      !tags.some((t) => t.id === selected.id)
    ) {
      setTags([...tags, selected]);
    }
  };

  const removeTag = (id: number) => {
    setTags(tags.filter((t) => t.id !== id));
  };



  const handleSave = async () => {

    const stripHtml = (html: string) => {
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    };

    try {
      const payload = {
        name,
        difficultyLevel: difficulty,
        tagsId: tags.map((t) => t.id),
        freeForms: questions.map((q) => ({
          title: stripHtml(q.text),
        })),
      };

      if (editId) {
        const payloadWithId = {
          ...payload,
          id: Number(editId),
        };
        await updateFreeFormAPI(payloadWithId);
      } else {
        await createFreeFormAPI(payload);
      }

      refreshList();
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  const [tagSearch, setTagSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(fetchQuestionsByTags({ pageNo: 1, perPage: 50, search: tagSearch, reset: true }));
    }, 500);
    return () => clearTimeout(handler);
  }, [tagSearch]);

  useEffect(() => {
    if (!editId) return;

    const loadData = async () => {
      const res = await fetchFreeFormByIdAPI(editId);

      setName(res?.data?.name || "");
      setDifficulty(res?.data?.difficultyLevel || "EASY");
      setTags(res?.data?.tags || []);

      setQuestions(
        res?.data?.freeForms?.map((q: any, index: number) => ({
          id: Date.now() + index,
          text: q.title.trim()
        })) || [{ id: Date.now(), text: "" }]
      );
    };

    loadData();
  }, [editId]);

  useEffect(() => {
    if (shouldScrollRef.current && modalBodyRef.current) {
      shouldScrollRef.current = false;
      const container = modalBodyRef.current;
      setTimeout(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }, 50);
    }
  }, [questions]);

  return (
    <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>

        {/* HEADER */}
        <div className={styles.modalHeader}>
          <h3>{editId ? "Edit free-form set" : "New free-form set"}</h3>
          <button onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        {/* BODY */}
        <div ref={modalBodyRef} className={styles.modalBody}>

          {/* NAME */}
          <div className={styles.inputGroup}>
            <label>Free Form Set Title</label>
            <input
              type="text"
              placeholder="e.g. Personal Information"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* DIFFICULTY */}
          <div className={styles.fullRow}>
            <label>Difficulty</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
            >
              <option value="EASY">Easy</option>
              <option value="MEDIUM">Medium</option>
              <option value="HARD">Hard</option>
            </select>
          </div>

          {/* TAGS */}
          <div className={styles.fullRow}>
            <label>Tags (max 5)</label>

            <ReactSelect
              inputValue={tagSearch}
              onInputChange={(val, { action }) => {
                if (action === 'input-change') setTagSearch(val);
              }}
              onChange={(selected: any) => {
                if (selected) {
                  handleTagSelect(selected.value);
                  setTagSearch("");
                }
              }}
              options={questionTags
                .filter((tag: Tag) => !tags.some((t) => t.id === tag.id))
                .map((tag: Tag) => ({ label: tag.name, value: tag.id }))}
              placeholder="Search and select tags..."
              value={null}
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: 'var(--surface-2, #f8fafc)',
                  borderColor: 'var(--border, #cbd5e1)',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  color: 'var(--text, #1e293b)',
                  '&:hover': { borderColor: 'var(--accent, #3b82f6)' }
                }),
                input: (base) => ({
                  ...base,
                  color: 'var(--text, #1e293b)',
                  margin: 0,
                  paddingTop: 0,
                  paddingBottom: 0,
                }),
                singleValue: (base) => ({
                  ...base,
                  color: 'var(--text, #1e293b)',
                }),
                placeholder: (base) => ({
                  ...base,
                  color: 'var(--text-3, #94a3b8)',
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
                menu: (base) => ({
                  ...base,
                  backgroundColor: 'var(--surface, #ffffff)',
                  border: '1px solid var(--border, #e2e8f0)',
                  zIndex: 9999,
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected
                    ? 'var(--accent, #3b82f6)'
                    : state.isFocused
                      ? 'var(--surface-2, #f1f5f9)'
                      : 'var(--surface, #ffffff)',
                  color: state.isSelected
                    ? '#ffffff'
                    : 'var(--text, #1e293b)',
                  cursor: 'pointer',
                  '&:active': {
                    backgroundColor: 'var(--accent, #3b82f6)'
                  }
                })
              }}
            />

            <div className={styles.tags}>
              {tags.map((tag) => (
                <span key={tag.id} className={styles.tag}>
                  {tag.name}
                  <span onClick={() => removeTag(tag.id)}>✖</span>
                </span>
              ))}
            </div>
          </div>

          {/* QUESTIONS (ONLY RICH TEXT) */}
          {questions.map((q, index) => (
            <div key={q.id} className={styles.questionBox}>

              <div className={styles.questionHeader}>
                <h4 className={styles.label}>
                  Question {index + 1}
                </h4>
                <button onClick={() => removeQuestion(q.id)}><DeleteIcon /></button>
              </div>

              <RichTextEditor
                value={q.text}
                onChange={(val) => updateQuestion(q.id, val)}
              />

              {/* TEXT RESPONSE LABEL */}
              <div className={styles.responseHint}>
                Text response only
              </div>
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div className={styles.modalFooter}>
          <button onClick={addFreeForm}>
            Add free-form
          </button>

          <button className={styles.saveBtn} onClick={handleSave}>
            Save question set
          </button>
        </div>
      </div>

      {deleteId !== null && (
        <div className={styles.modalOverlay} style={{ zIndex: 1100 }}>
          <div className={styles.modal} style={{ width: '400px', minHeight: 'unset', padding: '24px', textAlign: 'center' }}>
            <h3 style={{ marginTop: 0, color: 'var(--text, #1e293b)' }}>Confirm Delete</h3>
            <p style={{ color: 'var(--text-2, #475569)', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' }}>
              This is part of another assessment, so deleting it will also remove it from that assessment.            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button
                onClick={() => setDeleteId(null)}
                style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border, #e2e8f0)', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-2, #64748b)' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveQuestion}
                style={{ padding: '8px 16px', background: '#ef4444', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff', fontWeight: 'bold' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreeFormModal;